'use client'

import { Logo } from '@/components/ui/logo'
import { ThemeToggle } from '@/components/layout/theme-toggle'
import { ReactNode, InputHTMLAttributes } from 'react'
import { CheckCircle2, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

// ─── Left panel illustration — laptop / digital workspace ───────────────────────
const SPARKLE = 'M0,-7 C0.9,-2 2,-0.9 7,0 C2,0.9 0.9,2 0,7 C-0.9,2 -2,0.9 -7,0 C-2,-0.9 -0.9,-2 0,-7 Z'

function WorkspaceArt() {
  const card = 'var(--of-bg-card)'
  const border = 'var(--of-border)'
  const screen = 'var(--of-bg-app)'
  return (
    <svg
      viewBox="0 0 380 340"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: '100%', maxWidth: 360 }}
    >
      {/* Soft glow + scattered dots */}
      <circle cx="190" cy="150" r="134" style={{ fill: 'var(--of-violet)', fillOpacity: 0.05 }} />
      {([[28,40],[352,52],[40,302],[348,296],[18,182],[364,200],[192,26],[112,312]] as [number,number][])
        .map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="1.9" style={{ fill: 'var(--of-fg-3)', fillOpacity: 0.22 }} />
        ))}

      {/* Sparkles */}
      <path d={SPARKLE} transform="translate(306,50)" style={{ fill: 'var(--of-violet)' }} />
      <path d={SPARKLE} transform="translate(64,212) scale(0.8)" style={{ fill: 'var(--of-cyan)' }} />
      <path d={SPARKLE} transform="translate(26,158) scale(0.6)" style={{ fill: 'var(--of-violet)' }} />

      {/* Laptop shadow */}
      <ellipse cx="190" cy="218" rx="134" ry="9" style={{ fill: 'var(--of-ink-08)' }} />

      {/* Laptop screen */}
      <rect x="86" y="54" width="208" height="140" rx="13" fill={card} stroke={border} strokeWidth="1.6" />
      <rect x="96" y="64" width="188" height="120" rx="7" fill={screen} />

      {/* — screen header — */}
      <rect x="108" y="76" width="15" height="15" rx="4.5" style={{ fill: 'var(--of-violet)' }} />
      <rect x="129" y="78.5" width="42" height="4.5" rx="2.25" style={{ fill: 'var(--of-border-strong)' }} />
      <rect x="129" y="87" width="28" height="3.5" rx="1.75" fill={border} />
      <rect x="234" y="77" width="38" height="13" rx="6.5" style={{ fill: 'var(--of-violet-soft)' }} />

      {/* — screen stat cards — */}
      {([
        ['var(--of-violet)', 108], ['var(--of-teal)', 165], ['var(--of-amber)', 222],
      ] as [string, number][]).map(([accent, x], i) => (
        <g key={i}>
          <rect x={x} y="102" width="49" height="34" rx="6" fill={card} stroke={border} strokeWidth="1.2" />
          <rect x={x + 9} y="110" width="15" height="3.5" rx="1.75" fill={accent} />
          <rect x={x + 9} y="118" width="26" height="9" rx="3" fill={accent} fillOpacity="0.85" />
          <rect x={x + 9} y="130" width="20" height="2.5" rx="1.25" fill={border} />
        </g>
      ))}

      {/* — screen chart — */}
      <defs>
        <linearGradient id="of-wa-chart" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--of-violet)" stopOpacity="0.3" />
          <stop offset="100%" stopColor="var(--of-violet)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d="M108,165 L141,153 L174,160 L207,147 L240,157 L272,149 L272,172 L108,172 Z" fill="url(#of-wa-chart)" />
      <path
        d="M108,165 L141,153 L174,160 L207,147 L240,157 L272,149"
        style={{ stroke: 'var(--of-violet)' }}
        strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
      />
      {([[108,165],[141,153],[174,160],[207,147],[240,157],[272,149]] as [number,number][]).map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="2.6" style={{ fill: 'var(--of-violet)' }} stroke={card} strokeWidth="1.6" />
      ))}

      {/* Laptop base */}
      <rect x="172" y="194" width="36" height="4" rx="2" fill={border} />
      <rect x="64" y="196" width="252" height="13" rx="6.5" fill={card} stroke={border} strokeWidth="1.6" />

      {/* Floating card — task done */}
      <rect x="8" y="98" width="74" height="46" rx="12" fill={card} stroke={border} strokeWidth="1.4" />
      <circle cx="31" cy="121" r="11" style={{ fill: 'var(--of-mint)' }} />
      <path d="M26,121 l3.6,3.6 l6.6,-7.2" stroke={card} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <rect x="49" y="114" width="26" height="4.5" rx="2.25" style={{ fill: 'var(--of-border-strong)' }} />
      <rect x="49" y="123" width="17" height="3.5" rx="1.75" fill={border} />

      {/* Floating card — progress donut */}
      <rect x="300" y="104" width="72" height="72" rx="16" fill={card} stroke={border} strokeWidth="1.4" />
      <circle cx="336" cy="140" r="19" fill="none" style={{ stroke: 'var(--of-violet-soft)' }} strokeWidth="7" />
      <circle
        cx="336" cy="140" r="19" fill="none"
        style={{ stroke: 'var(--of-violet)' }}
        strokeWidth="7" strokeLinecap="round"
        strokeDasharray="83 119" transform="rotate(-90 336 140)"
      />
      <circle cx="336" cy="140" r="4" style={{ fill: 'var(--of-violet)' }} />

      {/* Coffee mug */}
      <path d="M55,256 a8,8 0 0 1 0,16" fill="none" stroke={border} strokeWidth="3" />
      <rect x="24" y="246" width="32" height="30" rx="7" fill={card} stroke={border} strokeWidth="1.5" />
      <ellipse cx="40" cy="246" rx="16" ry="3.6" fill={screen} stroke={border} strokeWidth="1.5" />
      <rect x="24" y="262" width="32" height="6" style={{ fill: 'var(--of-violet)', fillOpacity: 0.7 }} />
      <path d="M34,239 q3,-5 0,-10" fill="none" style={{ stroke: 'var(--of-fg-faint)' }} strokeWidth="1.7" strokeLinecap="round" />
      <path d="M45,239 q3,-5 0,-10" fill="none" style={{ stroke: 'var(--of-fg-faint)' }} strokeWidth="1.7" strokeLinecap="round" />

      {/* Desk plant */}
      <ellipse cx="332" cy="244" rx="6.5" ry="13" transform="rotate(-28 332 244)" style={{ fill: 'var(--of-mint)' }} fillOpacity="0.9" />
      <ellipse cx="350" cy="244" rx="6.5" ry="13" transform="rotate(28 350 244)" style={{ fill: 'var(--of-teal)' }} fillOpacity="0.9" />
      <ellipse cx="341" cy="234" rx="6.5" ry="15" style={{ fill: 'var(--of-mint)' }} />
      <path d="M327,258 L355,258 L351,280 L331,280 Z" fill={card} stroke={border} strokeWidth="1.5" />
      <rect x="324" y="254" width="34" height="6" rx="2" fill={card} stroke={border} strokeWidth="1.5" />
    </svg>
  )
}

// ─── Shared form primitives ────────────────────────────────────────────────────
const LABEL_STYLE: React.CSSProperties = {
  display: 'block',
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: 'var(--of-fg-3)',
  marginBottom: 6,
  fontFamily: 'var(--of-font-body)',
}

// Borderless, filled input — visible on a white page without a hard border.
const INPUT_BASE: React.CSSProperties = {
  width: '100%',
  height: 46,
  fontSize: 13.5,
  fontFamily: 'var(--of-font-body)',
  background: 'var(--of-bg-app)',
  color: 'var(--of-fg-1)',
  border: '1.5px solid transparent',
  borderRadius: 11,
  outline: 'none',
  transition: 'border-color 140ms, box-shadow 140ms, background 140ms',
  paddingLeft: 42,
  paddingRight: 14,
}

interface AuthFieldProps {
  id: string
  label: string
  type?: string
  placeholder: string
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  autoComplete?: string
  icon: ReactNode
  trailing?: ReactNode
  hasError?: boolean
}

export function AuthField({
  id, label, type = 'text', placeholder, value, onChange,
  disabled, autoComplete, icon, trailing, hasError,
}: AuthFieldProps) {
  return (
    <div>
      <label htmlFor={id} style={LABEL_STYLE}>{label}</label>
      <div style={{ position: 'relative' }}>
        <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--of-fg-3)', display: 'flex', alignItems: 'center' }}>
          {icon}
        </span>
        <input
          id={id}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          autoComplete={autoComplete}
          style={{
            ...INPUT_BASE,
            paddingRight: trailing ? 42 : 14,
            borderColor: hasError ? 'var(--of-coral)' : 'transparent',
          }}
          onFocus={e => {
            e.currentTarget.style.borderColor = 'var(--of-violet)'
            e.currentTarget.style.background = 'var(--of-bg-card)'
            e.currentTarget.style.boxShadow = '0 0 0 3px color-mix(in srgb, var(--of-violet) 14%, transparent)'
          }}
          onBlur={e => {
            e.currentTarget.style.borderColor = hasError ? 'var(--of-coral)' : 'transparent'
            e.currentTarget.style.background = 'var(--of-bg-app)'
            e.currentTarget.style.boxShadow = ''
          }}
        />
        {trailing && (
          <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}>
            {trailing}
          </span>
        )}
      </div>
    </div>
  )
}

interface AuthButtonProps {
  children: ReactNode
  type?: 'submit' | 'button'
  disabled?: boolean
  isLoading?: boolean
  onClick?: () => void
  variant?: 'primary' | 'outline'
}

export function AuthButton({ children, type = 'submit', disabled, isLoading, onClick, variant = 'primary' }: AuthButtonProps) {
  return (
    <Button
      type={type}
      onClick={onClick}
      disabled={disabled || isLoading}
      variant={variant === 'primary' ? 'default' : 'outline'}
      size="lg"
      className="w-full"
    >
      {isLoading && <Loader2 className="animate-spin" />}
      {children}
    </Button>
  )
}

interface AuthEyebrowProps { children: ReactNode }
export function AuthEyebrow({ children }: AuthEyebrowProps) {
  return (
    <p style={{
      fontSize: 10.5, fontWeight: 600, letterSpacing: '0.18em',
      textTransform: 'uppercase', color: 'var(--of-violet)',
      marginBottom: 14, fontFamily: 'var(--of-font-body)',
    }}>
      {children}
    </p>
  )
}

interface AuthHeadingProps { title: string; sub: string }
export function AuthHeading({ title, sub }: AuthHeadingProps) {
  return (
    <div style={{ marginBottom: 28 }}>
      <h1 style={{
        fontSize: 28, fontFamily: 'var(--of-font-body)',
        letterSpacing: '-0.02em', lineHeight: 1.15,
        color: 'var(--of-fg-1)', margin: '0 0 6px',
      }}>
        {title}
      </h1>
      <p style={{ fontSize: 13.5, color: 'var(--of-fg-3)', margin: 0, lineHeight: 1.5 }}>
        {sub}
      </p>
    </div>
  )
}

interface AuthErrorProps { message: string | null }
export function AuthError({ message }: AuthErrorProps) {
  if (!message) return null
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 8,
      padding: '10px 14px', borderRadius: 10,
      background: 'color-mix(in srgb, var(--of-coral) 8%, transparent)',
      border: '1px solid color-mix(in srgb, var(--of-coral) 24%, transparent)',
      fontSize: 13, color: 'var(--of-coral-ink)',
      marginBottom: 16,
    }}>
      <span style={{ flexShrink: 0, marginTop: 1 }}>⚠</span>
      <span>{message}</span>
    </div>
  )
}

// ─── Left panel features ───────────────────────────────────────────────────────
const FEATURES = [
  'Sprint & Kanban boards',
  'Time tracking & reports',
  'Issue tracking & wiki',
  'Team collaboration',
]

// ─── Main shell ────────────────────────────────────────────────────────────────
interface AuthShellProps { children: ReactNode }

export function AuthShell({ children }: AuthShellProps) {
  return (
    <div className="h-screen overflow-hidden flex" style={{ background: 'var(--of-bg-card)' }}>

      {/* ── Left panel — illustration (white, divider on the right) ── */}
      <div
        className="of-auth-panel hidden lg:flex lg:w-[480px] xl:w-[540px] flex-col h-full overflow-hidden flex-shrink-0"
        style={{ borderRight: '1px solid var(--of-border)' }}
      >
        <div className="flex flex-col h-full relative">
          {/* Logo */}
          <div className="flex items-center h-16 flex-shrink-0 px-12">
            <Logo width={160} height={40} />
          </div>

          {/* Illustration */}
          <div className="flex-1 flex items-center justify-center px-12 py-4">
            <WorkspaceArt />
          </div>

          {/* Text + features */}
          <div className="flex-shrink-0 px-12 pb-12 space-y-6">
            <div>
              <h2 style={{
                fontSize: 23, fontFamily: 'var(--of-font-body)',
                letterSpacing: '-0.015em', lineHeight: 1.25,
                color: 'var(--of-fg-1)', margin: '0 0 8px',
              }}>
                Your work,<br />
                <span className="of-gradient-text">beautifully orchestrated.</span>
              </h2>
              <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--of-fg-3)', margin: 0 }}>
                Sprints, tasks, time tracking, and team collaboration — all in one place.
              </p>
            </div>

            {/* Feature chips — shared `of-chip` pill (primary at 0.10), identical
                to the pills used on the dashboard and across the app. */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {FEATURES.map(f => (
                <span key={f} className="of-chip">
                  <CheckCircle2 style={{ width: 11, height: 11, flexShrink: 0 }} />
                  {f}
                </span>
              ))}
            </div>

            {/* Stats */}
            <div style={{ display: 'flex', gap: 30 }}>
              {[
                { value: '∞', label: 'Projects' },
                { value: '100%', label: 'Open source' },
                { value: '24/7', label: 'Always on' },
              ].map(s => (
                <div key={s.label}>
                  <p style={{ fontSize: 17, fontFamily: 'var(--of-font-body)', color: 'var(--of-violet)', letterSpacing: '-0.02em', margin: '0 0 2px' }}>{s.value}</p>
                  <p style={{ fontSize: 11, color: 'var(--of-fg-3)', margin: 0 }}>{s.label}</p>
                </div>
              ))}
            </div>

            <p style={{ fontSize: 11, color: 'var(--of-fg-faint)', margin: 0 }}>
              © {new Date().getFullYear()} OrbitFlow
            </p>
          </div>
        </div>
      </div>

      {/* ── Right panel — form (borderless, no card, white) ─────── */}
      <div
        className="flex-1 flex flex-col h-full overflow-y-auto relative"
        style={{ background: 'var(--of-bg-card)' }}
      >
        {/* Top bar — borderless */}
        <div
          className="flex items-center justify-between flex-shrink-0"
          style={{ height: 64, paddingLeft: 28, paddingRight: 28 }}
        >
          <div className="flex items-center gap-2.5 lg:hidden">
            <Logo width={130} height={36} />
          </div>
          <div className="ml-auto">
            <ThemeToggle />
          </div>
        </div>

        {/* Form area — sits directly on the page, no card */}
        <div className="flex-1 flex items-center justify-center" style={{ padding: '24px 24px 64px' }}>
          <div style={{ width: '100%', maxWidth: 396 }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}
